import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useOrg } from "../../contexts/OrgContext";
import ThemeToggle from "../UI/ThemeToggle";
import toast from "react-hot-toast";
import {
  HiOutlineHome,
  HiOutlineUsers,
  HiOutlinePhone,
  HiOutlineDocumentReport,
  HiOutlineMenu,
  HiOutlineX,
  HiOutlineClipboardCheck,
  HiOutlineLogout,
  HiOutlineClipboardList,
  HiOutlineCog,
  HiOutlineSparkles,
  HiOutlineMicrophone,
  HiOutlineCube,
  HiOutlineDocumentText,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineChevronDown,
  HiOutlineOfficeBuilding,
  HiOutlineChat,
  HiOutlineChartBar,
  HiOutlineAdjustments,
  HiOutlineCalendar,
  HiOutlineDownload,
  HiOutlineUserGroup,
  HiOutlineLightBulb,
  HiOutlineExclamation,
  HiOutlineTicket,
  HiOutlineShieldCheck,
  HiOutlineGift,
  HiOutlineSwitchHorizontal,
  HiOutlinePlus,
  HiOutlineLocationMarker,
} from "react-icons/hi";
import { API_ORIGIN, accountSwitchAPI } from "../../services/api";
import { APP_CONFIG } from "../../pages/public/config/constants";
import logo from "../../assets/logo.png";

// Module-level scroll position cache — survives component remounts
// because each admin page mounts its own Layout/Sidebar instance.
let _sidebarScrollTop = 0;

// Module-level expanded groups cache — preserves collapse/expand state
// across page navigations that remount the Sidebar component.
let _sidebarExpandedGroups = null;

// Role hierarchy: telecaller < manager < admin < super_admin
// minRole: the minimum role required to see this item (blank = visible to all)
const ROLE_LEVELS = { telecaller: 0, manager: 1, admin: 2, super_admin: 3 };

// Navigation structure: parent groups with children
// Items without children are standalone links
const navGroups = [
  {
    id: "community",
    label: "Join Community",
    icon: HiOutlineUserGroup,
    href: APP_CONFIG.COMMUNITY_WHATSAPP_URL,
    external: true,
    standalone: true,
  },
   // CEO Dashboard commented out — replaced by the Analytics section below.
   // (page still exists at src/pages/CEODashboard/CEODashboard.jsx and its
   // route in App.js is untouched — re-enable by uncommenting)
   // {
   //   id: "ceo-dashboard",
   //   label: "CEO Dashboard",
   //   icon: HiOutlineChartBar,
   //   path: "/app/ceo-dashboard",
   //   exact: true,
   //   standalone: true,
   //   minRole: "manager",
   // },
  {
    id: "dashboard",
    label: "Dashboard",
    icon: HiOutlineHome,
    path: "/app/",
    exact: true,
    standalone: true,
  },
  {
    id: "analytics",
    label: "Analytics",
    icon: HiOutlineChartBar,
    path: "/app/analytics",
    exact: true,
    standalone: true,
    minRole: "manager",
  },
  {
    id: "crm",
    label: "CRM",
    icon: HiOutlineOfficeBuilding,
    children: [
      { path: "/app/leads", label: "Leads", icon: HiOutlineUsers, exact: false },
      { path: "/app/visits", label: "Visits & Meetings", icon: HiOutlineLocationMarker, exact: false },
      { path: "/app/users", label: "Users", icon: HiOutlineUsers, exact: false, minRole: "manager" },
    ],
  },
  {
    id: "communication",
    label: "Communication",
    icon: HiOutlineChat,
    children: [
      { path: "/app/call-logs", label: "Call Logs", icon: HiOutlinePhone, exact: false },
      { path: "/app/recordings", label: "Recordings", icon: HiOutlineMicrophone, exact: false },
      { path: "/app/call-analysis", label: "Call Analysis", icon: HiOutlineClipboardList, exact: false },
      { path: "/app/follow-ups", label: "Follow Ups", icon: HiOutlineCalendar, exact: false },
      // Escalations section disabled (commented out — not removed)
      // { path: "/app/escalations", label: "Escalations", icon: HiOutlineExclamation, exact: false, minRole: "manager" },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    icon: HiOutlineChartBar,
    minRole: "manager",
    children: [
      { path: "/app/reports", label: "Reports", icon: HiOutlineDocumentReport, exact: true },
      { path: "/app/reports/daily-work", label: "Daily Work", icon: HiOutlineClipboardList, exact: true },
      { path: "/app/leaderboard", label: "Leaderboard", icon: HiOutlineSparkles, exact: true },
      { path: "/app/attendance", label: "Attendance", icon: HiOutlineClipboardCheck, exact: false },
    ],
  },
  {
    id: "manage",
    label: "Manage",
    icon: HiOutlineAdjustments,
    minRole: "admin",
    children: [
      { path: "/app/products", label: "Products", icon: HiOutlineCube, exact: false },
      { path: "/app/scripts", label: "Scripts", icon: HiOutlineDocumentText, exact: false },
      { path: "/app/settings", label: "Settings", icon: HiOutlineCog, exact: false, feature: "googleSheetIntegration" },
      { path: "/app/support", label: "Support", icon: HiOutlineTicket, exact: false },
      { path: "/app/audit-logs", label: "Audit Logs", icon: HiOutlineShieldCheck, exact: false, feature: "auditLogs" },
      { path: "/app/?guide=open", label: "Quick Guide", icon: HiOutlineLightBulb, exact: true },
    ],
  },
  {
    id: "refer-and-earn",
    label: "Refer & Earn",
    icon: HiOutlineGift,
    path: "/app/refer-and-earn",
    exact: true,
    standalone: true,
  },
];

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggle }) => {
  const { user, logout, switchOrganization } = useAuth();
  const { canUseFeature, plan, loading, organization } = useOrg();
  const location = useLocation();

  // Account Switcher (docs/ACCOUNT_SWITCHER_PLAN.md) — lists sibling orgs for this
  // email, lazily loaded the first time the dropdown is opened.
  const [orgSwitcherOpen, setOrgSwitcherOpen] = useState(false);
  const [myOrganizations, setMyOrganizations] = useState(null);
  const [orgSwitcherLoading, setOrgSwitcherLoading] = useState(false);
  const [switchingTo, setSwitchingTo] = useState(null);

  // Linked Workspaces — self-serve "+ Create Workspace" (docs/LINKED_WORKSPACES_PLAN.md)
  // — DISABLED for now, feature planned for later. Commented out, not removed,
  // per explicit request.
  // const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);
  // const [newWorkspaceName, setNewWorkspaceName] = useState('');
  // const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  // Only an org's own admin can spin off a workspace, and only from the main
  // (non-workspace) organization — not from a workspace itself.
  // const canCreateWorkspace = user?.role === 'admin' && !organization?.billingParentOrganizationId;

  const toggleOrgSwitcher = async () => {
    const opening = !orgSwitcherOpen;
    setOrgSwitcherOpen(opening);
    if (opening && myOrganizations === null) {
      setOrgSwitcherLoading(true);
      try {
        const res = await accountSwitchAPI.myOrganizations();
        setMyOrganizations(res.data?.data || []);
      } catch (err) {
        console.error('Failed to load organizations:', err);
        setMyOrganizations([]);
      } finally {
        setOrgSwitcherLoading(false);
      }
    }
  };

  const handleSwitchOrganization = async (organizationId) => {
    setSwitchingTo(organizationId);
    const result = await switchOrganization(organizationId);
    if (!result.success) {
      setSwitchingTo(null);
      toast.error(result.error || 'Failed to switch organization');
    }
    // On success, switchOrganization() itself performs a full-page redirect.
  };

  // Linked Workspaces disabled — see state block above.
  // const handleCreateWorkspace = async () => {
  //   const name = newWorkspaceName.trim();
  //   if (!name) return;
  //   setCreatingWorkspace(true);
  //   try {
  //     const res = await accountSwitchAPI.createWorkspace(name);
  //     const newOrgId = res.data?.data?.organizationId;
  //     toast.success(`Workspace "${name}" created — switching in...`);
  //     // Jump straight into the new workspace, no extra login needed.
  //     const result = await switchOrganization(newOrgId);
  //     if (!result.success) {
  //       toast.error(result.error || 'Workspace created, but switching failed — use the switcher above.');
  //     }
  //   } catch (err) {
  //     toast.error(err.response?.data?.message || 'Failed to create workspace');
  //   } finally {
  //     setCreatingWorkspace(false);
  //     setShowCreateWorkspace(false);
  //     setNewWorkspaceName('');
  //   }
  // };

  // Preserve sidebar scroll position across page navigations.
  // Each admin page mounts its own Layout/Sidebar, so we use a module-level
  // variable that survives component unmount/remount cycles.
  const navRef = useRef(null);

  // Restore scroll position on mount (runs before paint)
  useLayoutEffect(() => {
    if (navRef.current && _sidebarScrollTop > 0) {
      navRef.current.scrollTop = _sidebarScrollTop;
    }
  }, []);

  // Save scroll position on every scroll event
  const handleNavScroll = (e) => {
    _sidebarScrollTop = e.target.scrollTop;
  };

  // Make sure the active item is visible (it can sit below the fold, behind the
  // footer, when the restored scroll position doesn't include it).
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const activeEl = nav.querySelector('[data-nav-active="true"]');
    if (!activeEl) return;
    const navRect = nav.getBoundingClientRect();
    const elRect = activeEl.getBoundingClientRect();
    if (elRect.top < navRect.top || elRect.bottom > navRect.bottom) {
      activeEl.scrollIntoView({ block: 'nearest' });
      _sidebarScrollTop = nav.scrollTop;
    }
  }, [location.pathname]);

  // Save scroll position on unmount as a safety net
  useEffect(() => {
    return () => {
      if (navRef.current) {
        _sidebarScrollTop = navRef.current.scrollTop;
      }
    };
  }, []);

  // Track which groups are expanded — restore from cache or default to all open
  const [expandedGroups, setExpandedGroups] = useState(() => {
    if (_sidebarExpandedGroups) {
      return _sidebarExpandedGroups;
    }
    const initiallyExpanded = {};
    navGroups.forEach((group) => {
      if (group.children) {
        initiallyExpanded[group.id] = true;
      }
    });
    return initiallyExpanded;
  });

  const isActive = (path, exact = false) => {
    if (path === "/") return location.pathname === "/";
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleNavClick = () => {
    if (window.innerWidth < 1024) onClose();
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      _sidebarExpandedGroups = next;
      return next;
    });
  };

  // Download App state
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadApp = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const res = await fetch(`${API_ORIGIN}/api/app-version/latest`);
      const json = await res.json();
      if (json.success && json.data?.filePath) {
        // Build the download URL from the current backend origin so the file
        // is served from the same server the admin is connected to (the
        // backend's downloadUrl may point to a different domain like televalley.in).
        const downloadUrl = `${API_ORIGIN}/uploads/app/${json.data.filePath}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `televalley-v${json.data.version || 'latest'}.apk`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('No app version available for download.');
      }
    } catch {
      alert('Failed to fetch app version. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Filter nav items based on plan features and user role
  const userRoleLevel = ROLE_LEVELS[user?.role] ?? 0;

  const filterChildren = (children) => {
    return children.filter((item) => {
      // Filter by role
      if (item.minRole && (ROLE_LEVELS[item.minRole] ?? 0) > userRoleLevel) {
        return false;
      }
      // if (item.feature) {
      //   return canUseFeature(item.feature);
      // }
      return true;
    });
  };

  // Filter groups: hide entire group if no children visible (or group minRole not met)
  const visibleNavGroups = navGroups.filter((group) => {
    if (group.external || group.standalone) {
      // Standalone items: check minRole if present
      if (group.minRole && (ROLE_LEVELS[group.minRole] ?? 0) > userRoleLevel) {
        return false;
      }
      return true;
    }
    if (group.minRole && (ROLE_LEVELS[group.minRole] ?? 0) > userRoleLevel) {
      return false;
    }
    const children = filterChildren(group.children);
    return children.length > 0;
  });

  // Debug: Log plan info
  React.useEffect(() => {
    if (!loading && plan) {
      console.log('[Sidebar] Plan loaded:', plan.name, 'Features:', plan.features);
    }
  }, [loading, plan]);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-secondary-900/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Professional Design with Collapsible */}
      <aside
        className={`
          fixed left-0 z-50 top-0 lg:top-0
          bg-gradient-to-b from-white to-secondary-50/30 border-r border-secondary-200/50
          flex flex-col shadow-xl lg:shadow-none
          transform transition-all duration-300 ease-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          ${isCollapsed ? "lg:w-20" : "lg:w-64"}
          lg:translate-x-0
          h-screen lg:h-screen
        `}
        style={{ width: isCollapsed ? '5rem' : '16rem' }}
      >
        {/* Logo & Toggle Button */}
        <div className="relative flex items-center justify-between px-3 py-4 border-b border-secondary-100/50">
          <div className={`flex items-center justify-center ${isCollapsed ? 'w-full' : ''}`}>
            {isCollapsed ? (
              // Icon-only view: Show small logo or initials
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-lg">TV</span>
              </div>
            ) : (
              // Expanded view: Show full logo (brand-logo keeps it on a light
              // chip in dark mode — the tagline ink is baked into the PNG)
              <img
                src={logo}
                alt="TeleValley"
                className="brand-logo w-full h-auto max-h-14 object-contain"
              />
            )}
          </div>

          {/* Close button for mobile */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-2 rounded-xl hover:bg-secondary-100 lg:hidden transition-colors"
            aria-label="Close sidebar"
          >
            <HiOutlineX className="w-5 h-5 text-secondary-400" />
          </button>
        </div>

        {/* Toggle button for desktop — vertically centered on sidebar */}
        <button
          onClick={onToggle}
          className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-white border border-secondary-200 rounded-full items-center justify-center shadow-md hover:bg-secondary-50 transition-colors z-10"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <HiOutlineChevronRight className="w-3.5 h-3.5 text-secondary-600" />
          ) : (
            <HiOutlineChevronLeft className="w-3.5 h-3.5 text-secondary-600" />
          )}
        </button>

        {/* User Info - Professional */}
        <div className={`px-3 py-3 ${isCollapsed ? 'px-2' : ''}`}>
          <div className={`px-3 py-3 rounded-xl bg-gradient-to-r from-primary-50 to-indigo-50 border border-primary-100/50 ${isCollapsed ? 'px-2' : ''}`}>
            <div className={`flex items-center gap-3 ${isCollapsed ? 'justify-center' : ''}`}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0 shadow-md ring-2 ring-white">
                <span className="text-white font-semibold text-sm">
                  {user?.firstName?.charAt(0)}
                  {user?.lastName?.charAt(0)}
                </span>
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-secondary-900 text-sm truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-secondary-500 truncate">
                    {user?.email}
                  </p>
                </div>
              )}
              {!isCollapsed && (
                <button
                  type="button"
                  onClick={toggleOrgSwitcher}
                  title="Switch organization"
                  className="p-1.5 rounded-lg text-secondary-400 hover:text-primary-600 hover:bg-white/60 transition-colors flex-shrink-0"
                >
                  <HiOutlineSwitchHorizontal className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Account Switcher dropdown — sibling orgs for this email */}
            {!isCollapsed && orgSwitcherOpen && (
              <div className="mt-2 pt-2 border-t border-primary-100/50 space-y-1">
                {orgSwitcherLoading ? (
                  <p className="text-xs text-secondary-400 px-2 py-1">Loading organizations...</p>
                ) : myOrganizations && myOrganizations.length > 0 ? (
                  myOrganizations.map((org) => (
                    <button
                      key={org.organizationId}
                      type="button"
                      disabled={org.isCurrent || switchingTo === org.organizationId}
                      onClick={() => handleSwitchOrganization(org.organizationId)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-left transition-colors disabled:cursor-default ${
                        org.isCurrent
                          ? 'bg-primary-100 text-primary-700 font-semibold'
                          : 'text-secondary-600 hover:bg-white/60'
                      }`}
                    >
                      <HiOutlineOfficeBuilding className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate flex-1">{org.name}</span>
                      {org.isCurrent && <span className="text-[10px]">Current</span>}
                      {switchingTo === org.organizationId && <span className="text-[10px]">Switching…</span>}
                    </button>
                  ))
                ) : (
                  <p className="text-xs text-secondary-400 px-2 py-1">No other organizations found for this email.</p>
                )}

                {/* Linked Workspaces — self-serve creation, billed under this org.
                    DISABLED for now, feature planned for later. Commented out,
                    not removed, per explicit request.
                {canCreateWorkspace && (
                  <div className="pt-1 mt-1 border-t border-primary-100/50">
                    {!showCreateWorkspace ? (
                      <button
                        type="button"
                        onClick={() => setShowCreateWorkspace(true)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-left text-primary-600 hover:bg-white/60 font-medium"
                      >
                        <HiOutlinePlus className="w-3.5 h-3.5 flex-shrink-0" />
                        Create Workspace
                      </button>
                    ) : (
                      <div className="px-1 py-1 space-y-1.5">
                        <input
                          type="text"
                          autoFocus
                          value={newWorkspaceName}
                          onChange={(e) => setNewWorkspaceName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleCreateWorkspace(); }}
                          placeholder="Workspace name"
                          disabled={creatingWorkspace}
                          className="w-full px-2 py-1 text-xs rounded-lg border border-secondary-200 focus:outline-none focus:ring-1 focus:ring-primary-400"
                        />
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={handleCreateWorkspace}
                            disabled={creatingWorkspace || !newWorkspaceName.trim()}
                            className="flex-1 px-2 py-1 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                          >
                            {creatingWorkspace ? 'Creating…' : 'Create'}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowCreateWorkspace(false); setNewWorkspaceName(''); }}
                            disabled={creatingWorkspace}
                            className="px-2 py-1 text-xs font-medium rounded-lg text-secondary-500 hover:bg-white/60"
                          >
                            Cancel
                          </button>
                        </div>
                        <p className="text-[10px] text-secondary-400 px-0.5">
                          Billed under this organization — no separate plan needed.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                */}
              </div>
            )}
          </div>
        </div>

        {/* Navigation - Professional with grouped items */}
        <nav ref={navRef} onScroll={handleNavScroll} className="flex-1 overflow-y-auto px-3 py-2">
          {!isCollapsed && (
            <p className="px-3 text-xs font-semibold text-secondary-400 uppercase tracking-wider mb-2">
              Menu
            </p>
          )}
          <ul className="space-y-1">
            {visibleNavGroups.map((group) => {
              // External link (opens in new tab — e.g. WhatsApp Community)
              if (group.external) {
                const Icon = group.icon;
                return (
                  <li key={group.id}>
                    <a
                      href={group.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleNavClick}
                      className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-xl
                        transition-all duration-200 relative
                        text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900
                        ${isCollapsed ? 'justify-center px-2' : ''}
                      `}
                      title={isCollapsed ? group.label : undefined}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0 text-secondary-400" />
                      {!isCollapsed && (
                        <span className="text-sm font-medium">{group.label}</span>
                      )}
                    </a>
                  </li>
                );
              }

              // Standalone item (no children)
              if (group.standalone) {
                const Icon = group.icon;
                const active = isActive(group.path, group.exact);
                return (
                  <li key={group.id}>
                    <NavLink
                      to={group.path}
                      data-nav-active={active ? "true" : undefined}
                      onClick={handleNavClick}
                      className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-xl
                        transition-all duration-200 relative
                        ${isCollapsed ? 'justify-center px-2' : ''}
                        ${
                          active
                            ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/25"
                            : "text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900"
                        }
                      `}
                      title={isCollapsed ? group.label : undefined}
                    >
                      {active && !isCollapsed && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/30 rounded-r-full" />
                      )}
                      <Icon
                        className={`w-5 h-5 flex-shrink-0 ${active ? "text-white" : "text-secondary-400"}`}
                      />
                      {!isCollapsed && (
                        <span className="text-sm font-medium">{group.label}</span>
                      )}
                    </NavLink>
                  </li>
                );
              }

              // Group with children
              const children = filterChildren(group.children);
              if (children.length === 0) return null;

              const GroupIcon = group.icon;
              const isGroupActive = children.some(
                (child) => isActive(child.path, child.exact)
              );
              const isExpanded = expandedGroups[group.id];

              // When collapsed, show group icon that opens tooltip-style flyout
              if (isCollapsed) {
                return (
                  <li key={group.id} className="relative group/sidebar">
                    <button
                      className={`
                        flex items-center justify-center w-full px-2 py-2.5 rounded-xl
                        transition-all duration-200
                        ${
                          isGroupActive
                            ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/25"
                            : "text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900"
                        }
                      `}
                      title={group.label}
                      onClick={() => toggleGroup(group.id)}
                    >
                      <GroupIcon
                        className={`w-5 h-5 flex-shrink-0 ${isGroupActive ? "text-white" : "text-secondary-400"}`}
                      />
                    </button>
                    {/* Flyout menu on hover when collapsed */}
                    {isExpanded && (
                      <div className="absolute left-full top-0 ml-2 w-48 bg-white rounded-xl shadow-lg border border-secondary-200/50 py-2 z-[60]">
                        <p className="px-3 py-1.5 text-xs font-semibold text-secondary-400 uppercase tracking-wider">
                          {group.label}
                        </p>
                        {children.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = isActive(child.path, child.exact);
                          return (
                            <NavLink
                              key={child.path}
                              to={child.path}
                              data-nav-active={childActive ? "true" : undefined}
                              onClick={handleNavClick}
                              className={`
                                flex items-center gap-2.5 px-3 py-2 mx-1 rounded-lg text-sm
                                transition-colors duration-150
                                ${
                                  childActive
                                    ? "bg-primary-50 text-primary-700 font-medium"
                                    : "text-secondary-600 hover:bg-secondary-50"
                                }
                              `}
                            >
                              <ChildIcon className="w-4 h-4" />
                              {child.label}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </li>
                );
              }

              // Expanded sidebar: show parent + collapsible children
              return (
                <li key={group.id}>
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className={`
                      flex items-center gap-3 w-full px-3 py-2.5 rounded-xl
                      transition-all duration-200 text-left
                      ${
                        isGroupActive
                          ? "text-primary-700 bg-primary-50/50"
                          : "text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900"
                      }
                    `}
                  >
                    <GroupIcon
                      className={`w-5 h-5 flex-shrink-0 ${isGroupActive ? "text-primary-500" : "text-secondary-400"}`}
                    />
                    <span className="text-sm font-medium flex-1">{group.label}</span>
                    <HiOutlineChevronDown
                      className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${
                        isExpanded ? "rotate-180" : ""
                      } ${isGroupActive ? "text-primary-400" : "text-secondary-400"}`}
                    />
                  </button>
                  {isExpanded && (
                    <ul className="mt-1 space-y-0.5 pl-4">
                      {children.map((child) => {
                        const ChildIcon = child.icon;
                        const childActive = isActive(child.path, child.exact);
                        return (
                          <li key={child.path}>
                            <NavLink
                              to={child.path}
                              data-nav-active={childActive ? "true" : undefined}
                              onClick={handleNavClick}
                              className={`
                                flex items-center gap-2.5 px-3 py-2 rounded-lg
                                transition-all duration-200 text-sm relative
                                ${
                                  childActive
                                    ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-sm shadow-primary-500/20"
                                    : "text-secondary-500 hover:bg-secondary-100 hover:text-secondary-900"
                                }
                              `}
                            >
                              {childActive && (
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-white/30 rounded-r-full" />
                              )}
                              <ChildIcon
                                className={`w-4 h-4 flex-shrink-0 ${childActive ? "text-white" : "text-secondary-400"}`}
                              />
                              <span className="font-medium">{child.label}</span>
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Download App Button */}
        <div className="px-3 pt-2">
          <button
            onClick={handleDownloadApp}
            disabled={isDownloading}
            className={`
              flex items-center gap-3 w-full px-3 py-2.5 rounded-xl
              text-white bg-gradient-to-r from-primary-500 to-primary-600
              hover:from-primary-600 hover:to-primary-700
              shadow-md shadow-primary-500/25 hover:shadow-lg hover:shadow-primary-500/30
              transition-all duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              ${isCollapsed ? 'justify-center px-2' : ''}
            `}
            title={isCollapsed ? "Download App" : undefined}
          >
            {isDownloading ? (
              <svg className="w-5 h-5 flex-shrink-0 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <HiOutlineDownload className="w-5 h-5 flex-shrink-0" />
            )}
            {!isCollapsed && (
              <span className="text-sm font-medium">
                {isDownloading ? 'Downloading...' : 'Download App'}
              </span>
            )}
          </button>
        </div>

        {/* Logout - Professional */}
        <div className="p-3 border-t border-secondary-100/50">
          <button
            onClick={logout}
            className={`
              flex items-center gap-3 w-full px-3 py-2.5 rounded-xl
              text-secondary-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100
              hover:text-red-600 transition-all duration-200
              ${isCollapsed ? 'justify-center' : ''}
            `}
            title={isCollapsed ? "Logout" : undefined}
          >
            <HiOutlineLogout className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && (
              <span className="text-sm font-medium">Logout</span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export const MobileHeader = ({ onMenuClick }) => {
  const { user } = useAuth();

  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-lg border-b border-secondary-200/50 shadow-sm">
      <div className="flex items-center justify-between px-4 py-2.5">
        <button
          onClick={onMenuClick}
          className="p-2 -ml-2 rounded-xl text-secondary-600 hover:bg-secondary-100 transition-colors"
        >
          <HiOutlineMenu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="brand-logo-box w-8 h-8 rounded-xl overflow-hidden shadow-md flex items-center justify-center">
            <img
              src={logo}
              alt="TeleValley"
              className="w-full h-full object-contain"
            />
          </div>
          <span className="font-bold text-secondary-900">TeleValley</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Dark / light mode toggle — moon = dark, sun = light */}
          <ThemeToggle />
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-100 to-primary-50 flex items-center justify-center ring-2 ring-white shadow-sm">
            <span className="text-primary-600 font-semibold text-xs">
              {user?.firstName?.charAt(0)}
              {user?.lastName?.charAt(0)}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Sidebar;